/**
 * AI Operations Studio
 * 
 * The future of Leynix — Think Microsoft Office for running a home care agency.
 * 
 * VERSION 1.0 (Current): Skeleton and navigation only
 * VERSION 1.1 (Future): Full document generation with templates
 * 
 * Categories:
 * 🏢 Agency — Business setup, agency policies, contracts
 * 👥 HR — Handbooks, policies, job descriptions, performance reviews
 * 📁 Compliance — Policy manuals, audits, incident investigations
 * 📄 Licensing — CDSS packets, administrator changes, emergency plans
 * ❤️ Client Care — Client packets, service agreements, care plans
 * 💰 Finance — Payroll memos, billing letters, rate sheets, invoices
 * 📢 Marketing — Flyers, referral packets, branding materials
 * 📊 Executive — Reports, meeting minutes, board presentations
 * 📚 Training — Orientation packets, training plans, competency tests
 * 🛠 Custom — User-defined templates
 * 
 * Every generated document automatically includes:
 * - Agency branding
 * - Business rules
 * - Configured policies
 * - Operational settings
 * - Compliance requirements
 * - Version history
 * - Audit trail
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Building2, Users, FolderOpen, FileText, Heart, 
  DollarSign, Megaphone, BarChart3, GraduationCap, Wrench,
  Sparkles, ArrowRight, Lock, CheckCircle2, Clock,
  FileOutput, Wand2, AlertCircle
} from 'lucide-react';

// Category configurations
const CATEGORIES = [
  {
    id: 'agency',
    label: 'Agency',
    icon: Building2,
    color: 'bg-blue-500',
    description: 'Business setup, agency policies, contracts',
    documents: [
      { name: 'Agency Profile', status: 'available', description: 'Complete agency information packet' },
      { name: 'Agency License Application', status: 'coming_soon', description: 'CDSS licensing packet' },
      { name: 'Business Policies', status: 'coming_soon', description: 'Core business policies' },
      { name: 'Service Agreement Template', status: 'coming_soon', description: 'Client service agreements' },
    ],
  },
  {
    id: 'hr',
    label: 'HR',
    icon: Users,
    color: 'bg-purple-500',
    description: 'Handbooks, policies, job descriptions',
    documents: [
      { name: 'Employee Handbook', status: 'coming_soon', description: 'Complete employee handbook with all policies' },
      { name: 'Attendance Policy', status: 'coming_soon', description: 'Time and attendance policy' },
      { name: 'Progressive Discipline Policy', status: 'coming_soon', description: 'Employee discipline procedures' },
      { name: 'Job Description - HHA', status: 'coming_soon', description: 'Home Health Aide job description' },
      { name: 'Job Description - CNA', status: 'coming_soon', description: 'Certified Nursing Assistant job description' },
      { name: 'Performance Evaluation', status: 'coming_soon', description: 'Annual performance review form' },
      { name: 'Offer Letter', status: 'coming_soon', description: 'Employment offer letter template' },
      { name: 'Corrective Action', status: 'coming_soon', description: 'Employee corrective action form' },
    ],
  },
  {
    id: 'compliance',
    label: 'Compliance',
    icon: FolderOpen,
    color: 'bg-green-500',
    description: 'Policy manuals, audits, investigations',
    documents: [
      { name: 'Policy Manual', status: 'coming_soon', description: 'Complete policy and procedure manual' },
      { name: 'Internal Audit Checklist', status: 'coming_soon', description: 'Compliance self-audit' },
      { name: 'Corrective Action Plan', status: 'coming_soon', description: 'CAP for compliance findings' },
      { name: 'Incident Investigation', status: 'coming_soon', description: 'Root cause analysis report' },
      { name: 'Compliance Report', status: 'coming_soon', description: 'Monthly compliance summary' },
    ],
  },
  {
    id: 'licensing',
    label: 'Licensing',
    icon: FileText,
    color: 'bg-amber-500',
    description: 'CDSS packets, administrator changes',
    documents: [
      { name: 'CDSS Licensing Packet', status: 'coming_soon', description: 'Complete licensing application' },
      { name: 'Administrator Change', status: 'coming_soon', description: 'Admin change notification' },
      { name: 'Policy Update Notification', status: 'coming_soon', description: 'Licensing policy update' },
      { name: 'Emergency Plan', status: 'coming_soon', description: 'Emergency preparedness plan' },
    ],
  },
  {
    id: 'client_care',
    label: 'Client Care',
    icon: Heart,
    color: 'bg-red-500',
    description: 'Client packets, service agreements',
    documents: [
      { name: 'Client Admission Packet', status: 'coming_soon', description: 'New client intake package' },
      { name: 'Service Agreement', status: 'coming_soon', description: 'Client service agreement' },
      { name: 'Care Plan Summary', status: 'coming_soon', description: 'Care plan overview document' },
      { name: 'Caregiver Introduction', status: 'coming_soon', description: 'Introduce new caregiver to client' },
      { name: 'Discharge Summary', status: 'coming_soon', description: 'Client discharge documentation' },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    icon: DollarSign,
    color: 'bg-emerald-500',
    description: 'Payroll memos, billing letters, invoices',
    documents: [
      { name: 'Payroll Memo', status: 'coming_soon', description: 'Payroll announcement or clarification' },
      { name: 'Billing Letter', status: 'coming_soon', description: 'Client billing communication' },
      { name: 'Rate Sheet', status: 'coming_soon', description: 'Service rate schedule' },
      { name: 'Proposal', status: 'coming_soon', description: 'Service proposal for new client' },
      { name: 'Invoice Template', status: 'coming_soon', description: 'Custom invoice format' },
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing',
    icon: Megaphone,
    color: 'bg-pink-500',
    description: 'Flyers, referral packets, branding',
    documents: [
      { name: 'Marketing Flyer', status: 'coming_soon', description: 'Service promotion flyer' },
      { name: 'Referral Packet', status: 'coming_soon', description: 'Materials for referral sources' },
      { name: 'Agency Brochure', status: 'coming_soon', description: 'Company overview brochure' },
      { name: 'Social Media Post', status: 'coming_soon', description: 'Social media content' },
    ],
  },
  {
    id: 'executive',
    label: 'Executive',
    icon: BarChart3,
    color: 'bg-indigo-500',
    description: 'Reports, meeting minutes, presentations',
    documents: [
      { name: 'Executive Report', status: 'coming_soon', description: 'Monthly executive summary' },
      { name: 'Meeting Minutes', status: 'coming_soon', description: 'Meeting documentation' },
      { name: 'Board Report', status: 'coming_soon', description: 'Board of directors report' },
      { name: 'Strategic Plan', status: 'coming_soon', description: 'Business strategic plan' },
    ],
  },
  {
    id: 'training',
    label: 'Training',
    icon: GraduationCap,
    color: 'bg-cyan-500',
    description: 'Orientation packets, training plans',
    documents: [
      { name: 'Caregiver Orientation', status: 'coming_soon', description: 'New hire orientation packet' },
      { name: 'Training Plan', status: 'coming_soon', description: 'Individual training plan' },
      { name: 'Competency Assessment', status: 'coming_soon', description: 'Skills competency test' },
      { name: 'In-Service Outline', status: 'coming_soon', description: 'In-service training outline' },
    ],
  },
  {
    id: 'custom',
    label: 'Custom',
    icon: Wrench,
    color: 'bg-slate-500',
    description: 'Create your own templates',
    documents: [
      { name: 'Custom Document', status: 'coming_soon', description: 'Create a new document from scratch' },
      { name: 'Custom Template', status: 'coming_soon', description: 'Create reusable template' },
    ],
  },
];

const STATUS_STYLES = {
  available: { badge: 'bg-green-100 text-green-800', icon: CheckCircle2, label: 'Available' },
  coming_soon: { badge: 'bg-amber-100 text-amber-800', icon: Clock, label: 'Coming Soon' },
};

export default function AIOperationsStudio() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('agency');
  
  const currentCategory = CATEGORIES.find(c => c.id === activeCategory);
  
  const handleDocumentClick = (doc) => {
    if (doc.status === 'coming_soon') {
      // Show coming soon message
      return;
    }
    // Future: Navigate to document generation wizard
    // navigate(`/operations-studio/generate/${activeCategory}/${doc.name}`);
  };
  
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="container max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center">
                  <Sparkles className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold tracking-tight">AI Operations Studio</h1>
                  <p className="text-muted-foreground">
                    Create everything you need to run your agency
                  </p>
                </div>
              </div>
            </div>
            <Badge variant="outline" className="text-xs">
              <Clock className="h-3 w-3 mr-1" />
              V1.1 Preview
            </Badge>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="container max-w-7xl mx-auto px-4 py-8">
        {/* Vision Statement */}
        <Card className="mb-8 border-primary/20 bg-gradient-to-r from-primary/5 to-transparent">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Wand2 className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h2 className="font-semibold text-lg mb-2">The Future of Agency Operations</h2>
                <p className="text-muted-foreground mb-4">
                  Think Microsoft Office — but purpose-built for running a home care agency. 
                  Every document knows your agency, your policies, your branding, and your compliance requirements.
                  AI helps you build. You approve and save.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary">
                    <Building2 className="h-3 w-3 mr-1" />
                    Agency-Aware
                  </Badge>
                  <Badge variant="secondary">
                    <FileOutput className="h-3 w-3 mr-1" />
                    Templates
                  </Badge>
                  <Badge variant="secondary">
                    <FolderOpen className="h-3 w-3 mr-1" />
                    Records Center
                  </Badge>
                  <Badge variant="secondary">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Audit Trail
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Category Tabs */}
        <Tabs value={activeCategory} onValueChange={setActiveCategory} className="w-full">
          <div className="overflow-x-auto pb-2 -mx-4 px-4">
            <TabsList className="inline-flex h-auto gap-1 bg-transparent p-0 min-w-max">
              {CATEGORIES.map((category) => {
                const Icon = category.icon;
                return (
                  <TabsTrigger
                    key={category.id}
                    value={category.id}
                    className="data-[state=active]:bg-card data-[state=active]:shadow-sm rounded-lg px-4 py-3 flex items-center gap-2"
                    data-testid={`category-${category.id}`}
                  >
                    <div className={`h-6 w-6 rounded ${category.color} flex items-center justify-center`}>
                      <Icon className="h-4 w-4 text-white" />
                    </div>
                    <span className="hidden sm:inline">{category.label}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>
          
          {/* Category Content */}
          {CATEGORIES.map((category) => (
            <TabsContent key={category.id} value={category.id} className="mt-6">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className={`h-10 w-10 rounded-lg ${category.color} flex items-center justify-center`}>
                    <category.icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold">{category.label}</h2>
                    <p className="text-muted-foreground">{category.description}</p>
                  </div>
                </div>
              </div>
              
              {/* Document Grid */}
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {category.documents.map((doc, idx) => {
                  const statusConfig = STATUS_STYLES[doc.status];
                  const StatusIcon = statusConfig.icon;
                  const isAvailable = doc.status === 'available';
                  
                  return (
                    <Card 
                      key={idx}
                      className={`cursor-pointer transition-all hover:shadow-md ${
                        !isAvailable ? 'opacity-70' : ''
                      }`}
                      onClick={() => handleDocumentClick(doc)}
                      data-testid={`doc-${doc.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="h-10 w-10 rounded-lg bg-secondary flex items-center justify-center">
                            <FileText className="h-5 w-5 text-muted-foreground" />
                          </div>
                          <Badge className={statusConfig.badge}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {statusConfig.label}
                          </Badge>
                        </div>
                        <h3 className="font-medium mb-1">{doc.name}</h3>
                        <p className="text-sm text-muted-foreground">{doc.description}</p>
                        
                        {isAvailable && (
                          <Button size="sm" className="mt-4 w-full">
                            <Wand2 className="h-4 w-4 mr-2" />
                            Generate
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>
          ))}
        </Tabs>
        
        {/* How It Will Work */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              How It Will Work (V1.1)
            </CardTitle>
            <CardDescription>
              The document generation workflow when fully implemented
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-4">
              <div className="text-center p-4 rounded-lg bg-secondary/30">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <span className="font-bold text-primary">1</span>
                </div>
                <h4 className="font-medium mb-1">Choose</h4>
                <p className="text-sm text-muted-foreground">Select the document you need</p>
              </div>
              
              <div className="text-center p-4 rounded-lg bg-secondary/30">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <span className="font-bold text-primary">2</span>
                </div>
                <h4 className="font-medium mb-1">Answer</h4>
                <p className="text-sm text-muted-foreground">AI asks only for missing info</p>
              </div>
              
              <div className="text-center p-4 rounded-lg bg-secondary/30">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <span className="font-bold text-primary">3</span>
                </div>
                <h4 className="font-medium mb-1">Review</h4>
                <p className="text-sm text-muted-foreground">AI produces draft. You edit.</p>
              </div>
              
              <div className="text-center p-4 rounded-lg bg-secondary/30">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <span className="font-bold text-primary">4</span>
                </div>
                <h4 className="font-medium mb-1">Save</h4>
                <p className="text-sm text-muted-foreground">Saved to Records Center with history</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Footer */}
        <div className="mt-8 text-center text-sm text-muted-foreground">
          <Sparkles className="h-4 w-4 inline mr-2" />
          AI Operations Studio is part of the Leynix V1.1 roadmap.
          <br />
          Focus now: Enter A Touch of Angels operational data using Universal Intake.
        </div>
      </div>
    </div>
  );
}
